// Analytics utility functions for tracking user interactions

// Initialize analytics (placeholder for Google Analytics, Mixpanel, etc.)
export const initAnalytics = () => {
  if (typeof window !== "undefined") {
    // Placeholder for analytics initialization
    console.log("Analytics initialized");

    // Example: Google Analytics 4
    // gtag('config', 'GA_TRACKING_ID');

    // Example: Facebook Pixel
    // fbq('init', 'PIXEL_ID');
  }
};

// Track CTA clicks
export const trackCTAClick = (ctaName, location, additionalData = {}) => {
  const eventData = {
    event: "cta_click",
    cta_name: ctaName,
    cta_location: location,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    ...additionalData,
  };

  // Console log for development
  console.log("CTA Click Tracked:", eventData);

  // Send to analytics platforms
  if (typeof window !== "undefined") {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: ctaName,
        custom_parameter_1: location,
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq("track", "Lead", {
        content_name: ctaName,
        content_category: "CTA",
      });
    }

    // Custom analytics endpoint
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      fetch(`${apiUrl}/analytics/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventData),
      }).catch((error) => {
        console.warn("Analytics tracking failed:", error);
      });
    } catch (error) {
      console.warn("Analytics tracking error:", error);
    }
  }
};

// Track page views
export const trackPageView = (pageName, additionalData = {}) => {
  const eventData = {
    event: "page_view",
    page_name: pageName,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    referrer: document.referrer,
    ...additionalData,
  };

  console.log("Page View Tracked:", eventData);

  if (typeof window !== "undefined") {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("config", "GA_TRACKING_ID", {
        page_title: pageName,
        page_location: window.location.href,
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq("track", "PageView");
    }
  }
};

// Track form submissions
export const trackFormSubmission = (formName, formData = {}) => {
  const eventData = {
    event: "form_submission",
    form_name: formName,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    form_data: formData,
  };

  console.log("Form Submission Tracked:", eventData);

  if (typeof window !== "undefined") {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("event", "generate_lead", {
        event_category: "Form",
        event_label: formName,
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq("track", "CompleteRegistration", {
        content_name: formName,
      });
    }
  }
};

// Track section visibility (for engagement metrics)
export const trackSectionView = (sectionName, timeSpent = 0) => {
  const eventData = {
    event: "section_view",
    section_name: sectionName,
    time_spent: timeSpent,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
  };

  console.log("Section View Tracked:", eventData);

  if (typeof window !== "undefined") {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("event", "scroll", {
        event_category: "Engagement",
        event_label: sectionName,
        value: timeSpent,
      });
    }
  }
};

// Track user engagement metrics
export const trackEngagement = (action, category, label, value) => {
  const eventData = {
    event: "engagement",
    action,
    category,
    label,
    value,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
  };

  console.log("Engagement Tracked:", eventData);

  if (typeof window !== "undefined") {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag("event", action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  }
};

// Performance tracking
export const trackPerformance = () => {
  if (typeof window !== "undefined" && "performance" in window) {
    const navigation = performance.getEntriesByType("navigation")[0];
    const paint = performance.getEntriesByType("paint");

    const performanceData = {
      event: "performance_metrics",
      load_time: navigation.loadEventEnd - navigation.loadEventStart,
      dom_content_loaded:
        navigation.domContentLoadedEventEnd -
        navigation.domContentLoadedEventStart,
      first_paint: paint.find((p) => p.name === "first-paint")?.startTime || 0,
      first_contentful_paint:
        paint.find((p) => p.name === "first-contentful-paint")?.startTime || 0,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      user_agent: navigator.userAgent,
    };

    console.log("Performance Tracked:", performanceData);

    // Send performance data to analytics
    if (window.gtag) {
      window.gtag("event", "timing_complete", {
        name: "load_time",
        value: Math.round(performanceData.load_time),
      });
    }
  }
};

// Initialize performance tracking on page load
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    setTimeout(trackPerformance, 1000); // Wait 1 second after load
  });
}

export default {
  initAnalytics,
  trackCTAClick,
  trackPageView,
  trackFormSubmission,
  trackSectionView,
  trackEngagement,
  trackPerformance,
};
